import {
  statsManfreditesQueryLocationValues,
  StatsManfreditesQueryRequest
} from '@asgard/api-common';
import { getErrorMessage, HttpError, Logger } from '@asgard/ui-common';
import { Button, Header, Stack, StackItem } from '@asgard/ui-library';
import { faCalculator } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormCheckbox } from 'components/form/FormCheckbox';
import { FormCustomSelect } from 'components/form/FormCustomSelect';
import { PageContentContainer } from 'components/layout/Page.styled';
import HtmlTrans from 'components/shared/HtmlTrans';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ServiceManfreditesStats } from 'services/manfredites-stats/service.manfredites-stats';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { YourOfferSalaryFormStyles } from './YourOfferSalaryForm.styles';
import {
  YourOfferSalaryFormFields,
  YourOfferSalaryFormFieldsSchema,
  YourOfferSalaryFormProps
} from './YourOfferSalaryForm.types';

export const YourOfferSalaryForm: React.FC<YourOfferSalaryFormProps> = ({
  techs,
  roles,
  yearsOfExperience,
  onSuccess
}) => {
  const { t } = useI18n('your-offer-salary');
  const { addErrorToast } = useToasts();
  const { query, replace, locale } = useRouter();

  const submitRef = useRef<HTMLButtonElement>(null);

  const locations = useMemo(
    () =>
      statsManfreditesQueryLocationValues.map((location) => ({
        label: location,
        value: location
      })),
    []
  );

  const formMethods = useForm<YourOfferSalaryFormFields>({
    defaultValues: {
      techs: query.techs
        ? techs.filter(({ value }) =>
            query.techs?.toString().split(',').includes(value)
          )
        : [],
      withFluentEnglish: !!query.withFluentEnglish,
      isRemote: !!query.isRemote,
      location: query.location
        ? locations.find(({ value }) => query.location?.toString() === value)
        : undefined,
      role: query.role
        ? roles.find(({ value }) => query.role?.toString() === value)
        : undefined,
      yearsOfExperience: query.yearsOfExperience
        ? yearsOfExperience.find(
            ({ value }) => query.yearsOfExperience?.toString() === value
          )
        : undefined
    },
    resolver: zodResolver(YourOfferSalaryFormFieldsSchema)
  });

  const { reset, getValues } = formMethods;

  const onChangeUpdateQueryParams = useCallback(
    async (shared = false) => {
      const params = new URLSearchParams();
      const {
        role,
        techs,
        withFluentEnglish,
        isRemote,
        location,
        yearsOfExperience
      } = getValues();

      if (role) {
        params.append('role', role.value);
      }
      if (techs && techs.length > 0) {
        params.append('techs', techs.map((tech) => tech.value).join(','));
      }
      if (withFluentEnglish) {
        params.append('withFluentEnglish', String(withFluentEnglish));
      }
      if (isRemote) {
        params.append('isRemote', String(isRemote));
      }
      if (location && !isRemote) {
        params.append('location', location.value);
      }
      if (yearsOfExperience) {
        params.append('yearsOfExperience', yearsOfExperience.value);
      }

      if (shared) {
        params.append('shared', 'true');
      }

      await replace(
        {
          pathname: getAppUrl(APP_URLS.yourOfferSalary, locale),
          query: params.toString()
        },
        undefined,
        { shallow: true }
      );
    },
    [getValues, locale, replace]
  );

  const isRemote = formMethods.watch('isRemote');

  const onSubmit = useCallback(
    async (data: YourOfferSalaryFormFields) => {
      try {
        const res = await ServiceManfreditesStats.get({
          roleId: +data.role.value,
          techIds: data.techs.map((tech) => +tech.value),
          withFluentEnglish: data.withFluentEnglish,
          location:
            !data.isRemote && data.location
              ? (data.location
                  .value as StatsManfreditesQueryRequest['location'])
              : undefined
        });

        onSuccess(res, data.yearsOfExperience.value);
        onChangeUpdateQueryParams(true);
      } catch (ex) {
        Logger.error({
          context: 'YourOfferSalaryForm',
          message: 'Error submitting form',
          error: ex as HttpError
        });

        addErrorToast(
          t('your-offer-salary.messages.submit-error', {
            error: getErrorMessage(ex)
          }),
          t('your-offer-salary.messages.submit-error_title')
        );
      }
    },
    [onSuccess, onChangeUpdateQueryParams, addErrorToast, t]
  );

  useEffect(() => {
    reset({
      techs: query.techs
        ? techs.filter(({ value }) =>
            query.techs?.toString().split(',').includes(value)
          )
        : [],
      withFluentEnglish: !!query.withFluentEnglish,
      isRemote: !!query.isRemote,
      location: query.location
        ? locations.find(({ value }) => query.location?.toString() === value)
        : undefined,
      role: query.role
        ? roles.find(({ value }) => query.role?.toString() === value)
        : undefined,
      yearsOfExperience: query.yearsOfExperience
        ? yearsOfExperience.find(
            ({ value }) => query.yearsOfExperience?.toString() === value
          )
        : undefined
    });
  }, [locations, query, roles, techs, yearsOfExperience]);

  useEffect(() => {
    setTimeout(() => {
      if (submitRef.current && query.shared) {
        submitRef.current.click();
      }
    }, 200);
  }, [query.shared]);

  return (
    <YourOfferSalaryFormStyles.Root>
      <PageContentContainer containerSize="lg">
        <YourOfferSalaryFormStyles.Content>
          <FormProvider {...formMethods}>
            <StackItem>
              <Header as="h2" size="4xl" editorial align="center">
                {t('your-offer-salary.form-title')}
              </Header>

              <HtmlTrans
                ns="your-offer-salary"
                i18nKey="your-offer-salary.form-desc"
              />
            </StackItem>

            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
              <Stack direction="column">
                <Stack>
                  <StackItem className="third">
                    <FormCustomSelect
                      name="role"
                      options={roles}
                      input={{
                        isSmall: true,
                        placeholder: t(
                          'your-offer-salary.fields.role_placeholder'
                        ),
                        onChange: onChangeUpdateQueryParams
                      }}
                      formGroup={{
                        label: t('your-offer-salary.fields.role')
                      }}
                    />
                  </StackItem>

                  <StackItem className="third">
                    <FormCustomSelect
                      name="techs"
                      options={techs}
                      input={{
                        isSmall: true,
                        max: 2,
                        isMulti: true,
                        placeholder: t(
                          'your-offer-salary.fields.techs_placeholder'
                        ),
                        onChange: onChangeUpdateQueryParams
                      }}
                      formGroup={{
                        label: t('your-offer-salary.fields.techs')
                      }}
                    />
                  </StackItem>

                  <StackItem className="third">
                    <FormCustomSelect
                      name="yearsOfExperience"
                      options={yearsOfExperience}
                      input={{
                        isSmall: true,
                        placeholder: t(
                          'your-offer-salary.fields.years-of-experience_placeholder'
                        ),
                        onChange: onChangeUpdateQueryParams
                      }}
                      formGroup={{
                        label: t('your-offer-salary.fields.years-of-experience')
                      }}
                    />
                  </StackItem>
                </Stack>

                <Stack alignment="flex-start">
                  <StackItem className="half">
                    <Stack direction="column">
                      <FormCheckbox
                        name="withFluentEnglish"
                        input={{
                          label: t(
                            'your-offer-salary.fields.with-fluent-english'
                          ),
                          onChange: onChangeUpdateQueryParams
                        }}
                      />

                      <FormCheckbox
                        name="isRemote"
                        input={{
                          label: t('your-offer-salary.fields.remote'),
                          onChange: onChangeUpdateQueryParams
                        }}
                      />
                    </Stack>
                  </StackItem>

                  {!isRemote && (
                    <StackItem className="half">
                      <FormCustomSelect
                        name="location"
                        options={locations}
                        formGroup={{
                          label: t('your-offer-salary.fields.location')
                        }}
                        input={{
                          isSmall: true,
                          placeholder: t(
                            'your-offer-salary.fields.location_placeholder'
                          ),
                          onChange: onChangeUpdateQueryParams
                        }}
                      />
                    </StackItem>
                  )}
                </Stack>

                <Stack contentAlign="center">
                  <Button
                    type="submit"
                    schema="brand1"
                    label={
                      formMethods.formState.isSubmitting
                        ? t('your-offer-salary.actions.submitting')
                        : t('your-offer-salary.actions.submit')
                    }
                    disabled={
                      !formMethods.formState.isValid ||
                      formMethods.formState.isSubmitting
                    }
                    startIcon={faCalculator}
                    size="xs"
                    ref={submitRef}
                  />
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        </YourOfferSalaryFormStyles.Content>
      </PageContentContainer>
    </YourOfferSalaryFormStyles.Root>
  );
};
